import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbMessage } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import {
  type ChatSearchHit,
  type ChatSearchResultsPayload,
  parseChatSearchResultsPayload,
} from "../citations/chatSearchResultsSchema";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../util/dateFormat";

interface ChatSearchResultsRendererProps {
  block: StreamingBlock;
}

const hitPillClass = (hit: ChatSearchHit): string => {
  const base = "inline-flex items-center rounded-full px-2 py-0.5 text-xs shrink-0";
  if (hit.kind === "message" && hit.role === "assistant") return `${base} bg-brand/15 text-brand`;
  return `${base} bg-inset text-muted-text`;
};

const HitRow = (props: { hit: ChatSearchHit }) => {
  const rt = useChatRuntime();

  const hitLabel = (): string => {
    if (props.hit.kind === "title") return rt.t("chats.search.role.title");
    if (props.hit.role === "user") return rt.t("chats.search.role.you");
    return rt.t("chats.search.role.assistant");
  };

  return (
    <>
      <div class="flex items-baseline justify-between gap-3">
        <div class="flex min-w-0 items-center gap-2">
          <h3 class="min-w-0 truncate text-sm font-bold text-primary-text">
            {props.hit.chatTitle || rt.t("chats.search.hit.untitled")}
          </h3>
          <span class={hitPillClass(props.hit)}>{hitLabel()}</span>
        </div>
        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(props.hit.createdAt)}</span>
      </div>
      <p
        class="search-snippet mt-1 line-clamp-2 text-sm wrap-break-word text-secondary-text"
        innerHTML={props.hit.snippet}
      />
    </>
  );
};

/**
 * Card renderer for `MessageType.CHAT_SEARCH_RESULTS` blocks emitted by the
 * `search_chats` tool. Mirrors the SearchPage row styling so search results in
 * a chat thread match the standalone search page visually.
 */
export const ChatSearchResultsRenderer = (props: ChatSearchResultsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(() => {
    const finalText = props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined;
    return finalText ?? getBlockContent(props.block.blockId);
  });

  // safeParseBlockJson handles concatenated payloads gracefully and the runtime
  // type guard enforces the contract shipped by the backend.
  const payload = createMemo<ChatSearchResultsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseChatSearchResultsPayload(json);
  });

  // Parse failed but we still received a block; surface a quiet notice rather
  // than crashing the thread.
  const parseFailed = () => !!rawContent() && payload() === null;

  // Link semantics come from the host: bluesky supplies hrefs + router
  // navigation; a surface without either (chief) renders plain rows.
  const chatHref = (chatId: string, messageId: string | undefined): string | undefined =>
    rt.actions.chatCitationHref?.({ chatId, messageId: messageId ?? "" });

  // Left-click navigates within the SPA; modified clicks fall through to the
  // browser so middle-click / cmd-click still open a new tab from the href.
  const openChat = (e: MouseEvent, chatId: string, messageId: string | undefined) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (!rt.actions.navigateToChat) return;
    e.preventDefault();
    rt.actions.navigateToChat({ chatId, messageId: messageId ?? "" });
  };

  const resultCountLabel = (count: number): string =>
    count === 1 ? rt.t("chats.search_card.results_one") : rt.t("chats.search_card.results", { count: String(count) });

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("chats.search_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().hits.length > 0 || data().gateDenied}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbMessage class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("chats.search_card.title")}</span>
                <Show when={!data().gateDenied}>
                  <span class="ml-auto text-xs text-muted-text">{resultCountLabel(data().hits.length)}</span>
                </Show>
              </div>

              <Show
                when={!data().gateDenied}
                fallback={<div class="p-3 text-sm text-muted-text">{data().gateDenied}</div>}
              >
                <ul class="divide-y divide-primary-border">
                  <For each={data().hits}>
                    {(hit) => (
                      <li>
                        <Show
                          when={chatHref(hit.chatId, hit.messageId) || rt.actions.navigateToChat}
                          fallback={
                            <div class="block px-3 py-2">
                              <HitRow hit={hit} />
                            </div>
                          }
                        >
                          <a
                            href={chatHref(hit.chatId, hit.messageId) ?? "#"}
                            onClick={(e) => openChat(e, hit.chatId, hit.messageId)}
                            class="block px-3 py-2 transition-colors hover:bg-panel-hover"
                          >
                            <HitRow hit={hit} />
                          </a>
                        </Show>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
